import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { AgentDetail } from '../../types/agent-studio';
import { AgentStatusPill } from '../../components/agent-studio';
import {
  OverviewTab,
  VersionsTab,
  ConfigurationTab,
  SkillsTab,
  ExecutionsTab,
} from './AgentDetailTabs';

type TabId = 'overview' | 'versions' | 'config' | 'skills' | 'executions';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overview',    label: 'Обзор' },
  { id: 'versions',    label: 'Версии' },
  { id: 'config',      label: 'Конфигурация' },
  { id: 'skills',      label: 'Навыки' },
  { id: 'executions',  label: 'Запуски' },
];

function HeaderSkeleton() {
  return (
    <div style={{ marginTop: '12px', height: '60px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ background: 'var(--bg-subtle)', height: '14px', width: '200px', borderRadius: '4px' }} />
      <div style={{ background: 'var(--bg-subtle)', height: '28px', width: '320px', borderRadius: '4px' }} />
    </div>
  );
}

function buildErrorMessage(error: string, systemName: string): string {
  if (
    error.toLowerCase().includes('404') ||
    error.toLowerCase().includes('not found') ||
    error.toLowerCase().includes('не найден')
  ) {
    return `Агент «${systemName}» не найден. Проверьте имя или вернитесь к списку агентов.`;
  }
  return `Не удалось загрузить агента ${systemName}. ${error}`;
}

function ErrorState({ errorMessage, systemName }: { errorMessage: string; systemName: string }) {
  return (
    <div className="card" style={{ marginTop: '24px', padding: '32px', borderLeft: '3px solid var(--red)' }}>
      <div className="t-eyebrow" style={{ color: 'var(--red)' }}>Ошибка загрузки</div>
      <p className="t-body" style={{ marginTop: '8px' }}>
        {buildErrorMessage(errorMessage, systemName)}
      </p>
    </div>
  );
}

function HeaderBlock({ agent, systemName }: { agent: AgentDetail; systemName: string }) {
  const navigate = useNavigate();
  return (
    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <h1 className="t-display" style={{ margin: '0 0 8px 0' }}>{agent.display_name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AgentStatusPill status={agent.status} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-secondary)' }}>
            {systemName}
          </span>
          {agent.active_version && (
            <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>
              Активная версия: v{agent.active_version.version_number}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => navigate('/agents')}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '13px',
          color: 'var(--ink-secondary)',
          padding: '4px 0',
          flexShrink: 0,
        }}
      >
        ← Все агенты
      </button>
    </div>
  );
}

function TabStrip({ tabs, currentTab, onTabClick }: {
  tabs: typeof TABS;
  currentTab: TabId;
  onTabClick: (id: TabId) => void;
}) {
  return (
    <div
      style={{
        marginTop: '20px',
        display: 'flex',
        gap: '32px',
        borderBottom: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
        position: 'sticky',
        top: '0',
        background: 'var(--bg-base, transparent)',
        zIndex: 1,
      }}
    >
      {tabs.map(tab => {
        const isActive = tab.id === currentTab;
        return (
          <button
            key={tab.id}
            onClick={() => onTabClick(tab.id)}
            style={{
              background: 'none',
              border: 'none',
              padding: '12px 0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: isActive ? 600 : 400,
              color: isActive ? 'var(--navy)' : 'var(--ink-secondary)',
              borderBottom: isActive ? '3px solid var(--navy)' : '3px solid transparent',
              marginBottom: '-1px',
              transition: 'color 0.15s ease, border-color 0.15s ease',
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AgentStudioDetail() {
  const { system_name } = useParams<{ system_name: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rawHash = location.hash.replace('#', '') as TabId;
  const isValidTab = TABS.some(t => t.id === rawHash);
  const currentTab: TabId = isValidTab ? rawHash : 'overview';

  useEffect(() => {
    let cancelled = false;
    async function fetchAgent() {
      try {
        setLoading(true);
        setError(null);
        const detail = await api.getAgent(system_name!);
        if (!cancelled) setAgent(detail);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (system_name) fetchAgent();
    return () => { cancelled = true; };
  }, [system_name]);

  function setTab(tabId: TabId) {
    navigate(`${location.pathname}#${tabId}`, { replace: false });
  }

  return (
    <div style={{ padding: '24px' }}>
      <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)' }}>
        <Link to="/agents" style={{ color: 'inherit' }}>Студия агентов</Link>
        {' · '}
        Профиль агента
      </div>

      {loading && <HeaderSkeleton />}
      {!loading && error && <ErrorState errorMessage={error} systemName={system_name ?? ''} />}
      {!loading && !error && agent && (
        <>
          <HeaderBlock agent={agent} systemName={system_name!} />
          <TabStrip tabs={TABS} currentTab={currentTab} onTabClick={setTab} />
          <div style={{ marginTop: '24px' }}>
            {currentTab === 'overview'    && <OverviewTab agent={agent} />}
            {currentTab === 'versions'    && <VersionsTab systemName={system_name!} />}
            {currentTab === 'config'      && <ConfigurationTab agent={agent} />}
            {currentTab === 'skills'      && <SkillsTab agent={agent} />}
            {currentTab === 'executions'  && <ExecutionsTab systemName={system_name!} />}
          </div>
        </>
      )}
    </div>
  );
}
